package com.starparty.app.astro0.celestialobjects

import com.starparty.app.astro0.MathD
import com.starparty.app.astro0.SP_Time
import kotlin.math.abs
import kotlin.math.sqrt

open class Planet(protected var mSun: Sun) : OrbitalBody() {
    protected var mlonEcl = 0.0
    protected var mlatEcl = 0.0

    // Can be overridden for bodies where perturbations are significant.
    open fun addPerturbations() {}
    override fun recalculateEquatorialCoordinates(t: SP_Time) {
        recalculateOrbitalElements(t)
        val epoch = t.epoch
        var E0 = calculateFirstAproxOfE()
        var E1 = 0.0
        var closeEnough = false
        while (!closeEnough) {
            E1 =
                E0 - (E0 - mOEs[e] / MathD.DEG_TO_RAD * MathD.sinD(E0) - mOEs[M]) / (1 - mOEs[e] * MathD.cosD(
                    E0
                ))
            closeEnough = abs(E1 - E0) < 0.001
            E0 = E1
        }
        calculateDistanceAndTrueAnomaly(E1)
        var xh = m_r * (MathD.cosD(mOEs[N]) * MathD.cosD(
            m_v + mOEs[w]
        ) - MathD.sinD(mOEs[N]) * MathD.sinD(m_v + mOEs[w]) * MathD.cosD(
            mOEs[i]
        ))
        var yh = m_r * (MathD.sinD(mOEs[N]) * MathD.cosD(
            m_v + mOEs[w]
        ) + MathD.cosD(mOEs[N]) * MathD.sinD(m_v + mOEs[w]) * MathD.cosD(
            mOEs[i]
        ))
        var zh =
            m_r * (MathD.sinD(m_v + mOEs[w]) * MathD.sinD(
                mOEs[i]
            ))
        mlonEcl = MathD.atanD2(yh, xh)
        mlatEcl = MathD.atanD2(zh, sqrt(xh * xh + yh * yh))

        //todo decide if the following correction for precession is needed:
        //   double lon_corr = 3.82394E-5 * ( 365.2422 * ( epoch - 2000.0 ) - m_d);
        //   mlonEcl = mlonEcl + lon_corr;
        addPerturbations()

        // Calculate heliocentric position;
        xh = m_r * MathD.cosD(mlonEcl) * MathD.cosD(mlatEcl)
        yh = m_r * MathD.sinD(mlonEcl) * MathD.cosD(mlatEcl)
        zh = m_r * MathD.sinD(mlatEcl)
        val rs = mSun.m_r
        val lonsun = mSun.lonSun
        val xs = rs * MathD.cosD(lonsun)
        val ys = rs * MathD.sinD(lonsun)
        // todo precession?

        // Convert heliocentric to geocentric position.
        val xg = xh + xs
        val yg = yh + ys
        val zg = zh

        // convert ecliptic coordinates to equatorial coordinates.
        val ye = yg * MathD.cosD(mEcl) - zg * MathD.sinD(mEcl)
        val ze = yg * MathD.sinD(mEcl) + zg * MathD.cosD(mEcl)
        val raDegrees = MathD.atanD2(ye, xg)
        val decDegrees = MathD.atanD2(ze, sqrt(xg * xg + ye * ye))

        itsRA_Radians = raDegrees * MathD.DEG_TO_RAD
        itsDec_Radians = decDegrees * MathD.DEG_TO_RAD
    }

    override fun getSearchableObjectType(): String {
        return "Planet"
    }

    companion object {

        // Orbital elements of the Moon:
        val OE_MOON = OrbitalElementsData(
            125.1228, -0.0529538083,
            5.1454, 0.0,
            318.0634, 0.1643573223,
            60.2666, 0.0,
            0.054900, 0.0,
            115.3654, 13.0649929509
        )

        // Orbital elements of Mercury:
        val OE_MERCURY = OrbitalElementsData(
            48.3313, 3.24587E-5,
            7.0047, 5.00E-8,
            29.1241, 1.01444E-5,
            0.387098, 0.0,
            0.205635, 5.59E-10,
            168.6562, 4.0923344368
        )

        // Orbital elements of Venus:
        val OE_VENUS = OrbitalElementsData(
            76.6799, 2.46590E-5,
            3.3946, 2.75E-8,
            54.8910, 1.38374E-5,
            0.723330, 0.0,
            0.006773, -1.302E-9,
            48.0052, 1.6021302244
        )

        // Orbital elements of Mars:
        val OE_MARS = OrbitalElementsData(
            49.5574, 2.11081E-5,
            1.8497, -1.78E-8,
            286.5016, 2.92961E-5,
            1.523688, 0.0,
            0.093405, 2.516E-9,
            18.6021, 0.5240207766
        )

        // Orbital elements of Jupiter:
        val OE_JUPITER = OrbitalElementsData(
            100.4542, 2.76854E-5,
            1.3030, -1.557E-7,
            273.8777, 1.64505E-5,
            5.20256, 0.0,
            0.048498, 4.469E-9,
            19.8950, 0.0830853001
        )

        // Orbital elements of Saturn:
        val OE_SATURN = OrbitalElementsData(
            113.6634, 2.38980E-5,
            2.4886, -1.081E-7,
            339.3939, 2.97661E-5,
            9.55475, 0.0,
            0.055546, -9.499E-9,
            316.9670, 0.0334442282
        )

        // Orbital elements of Uranus:
        val OE_URANUS = OrbitalElementsData(
            74.0005, 1.3978E-5,
            0.7733, 1.9E-8,
            96.6612, 3.0565E-5,
            19.18171, -1.55E-8,
            0.047318, 7.45E-9,
            142.5905, 0.011725806
        )

        // Orbital elements of Neptune:
        val OE_NEPTUNE = OrbitalElementsData(
            131.7806, 3.0173E-5,
            1.7700, -2.55E-7,
            272.8461, -6.027E-6,
            30.05826, 3.313E-8,
            0.008606, 2.15E-9,
            260.2471, 0.005995147
        )
    }

}